import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [devUrl, setDevUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      if (data.dev_reset_url) setDevUrl(data.dev_reset_url);
      toast.success("If that email exists, a reset link has been sent");
    } catch (err) { toast.error("Failed", { description: formatApiError(err) }); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="aurora" />
      <form onSubmit={submit} className="glass rounded-2xl p-8 w-full max-w-md relative overflow-hidden z-10">
        <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-gradient-to-br from-indigo-500/25 to-fuchsia-500/10 blur-3xl" />
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <span className="font-display font-extrabold text-xl">SyncLine</span>
        </Link>
        <div className="text-xs uppercase tracking-widest text-mute mb-2">Password reset</div>
        <h2 className="font-display font-extrabold text-3xl mb-2">Forgot your password?</h2>
        <p className="text-sm text-mute mb-5">We&apos;ll send you a secure link to reset it.</p>

        <label className="block text-xs uppercase tracking-widest text-mute mb-1.5">Email</label>
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="forgot-email"
          className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2.5 mb-5 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
        <button disabled={loading || sent} data-testid="forgot-submit"
          className="w-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 px-4 py-2.5 rounded-md font-medium flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(79,70,229,0.35)]">
          {loading ? "Sending…" : sent ? "Sent ✓" : (<>Send reset link <ArrowRight className="w-4 h-4" /></>)}
        </button>
        {devUrl && (
          <div className="mt-5 p-3 rounded-md border border-amber-500/30 bg-amber-500/10 text-xs" data-testid="dev-reset-url">
            <div className="text-amber-300 uppercase tracking-widest text-[10px] mb-1">Dev mode · link generated</div>
            <a href={devUrl} className="font-mono text-amber-200 break-all hover:underline">{devUrl}</a>
          </div>
        )}
        <div className="mt-5 text-xs text-mute text-center">
          Remembered it? <button type="button" onClick={() => nav(-1)} className="text-white underline" data-testid="forgot-back-btn">Back</button>
        </div>
      </form>
    </div>
  );
}
